import { useState, type ReactNode } from 'react';

import { Button } from '../../button';

import {
  agreementLine,
  canGradeNext,
  codingSummary,
  codingTransition,
  FAILURE_CARDS,
  FAILURE_MODES,
  failureModeLabels,
  gradedNotes,
  initialCoding,
  initialMatch,
  initialRun,
  initialSampling,
  MATCH_RULES,
  matchExpectedTasks,
  matchRows,
  matchRuleLabels,
  matchSummary,
  matchText,
  matchTransition,
  matchVerdict,
  modeLine,
  modeTable,
  NOTE_COUNT,
  reasonsText,
  RUN_VIEWS,
  runningLine,
  runSummary,
  runText,
  runTransition,
  runViewLabels,
  SAMPLED_NOTES,
  SAMPLING_NOTE_IDS,
  SAMPLING_RUNS,
  samplingRunLabels,
  samplingSummary,
  scoreFor,
  selectedTask,
  shownRuns,
  toExpectedTaskId,
  toFailureMode,
  toMatchRule,
  totalsLine,
  toRunView,
  toSamplingNoteId,
  toSamplingRun,
  type CodingAction,
  type MatchAction,
  type RunAction,
} from './eval-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './eval-labs.css';

function Predict({ children }: { children: ReactNode }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
  empty = 'No rows',
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
  empty?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">{empty}</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs of the golden set.';

export function EvalMatchLab() {
  const [state, setState] = useState(initialMatch);
  const act = (action: MatchAction) => setState((current) => matchTransition(current, action));
  const task = selectedTask(state);
  const summary = matchSummary(state);
  return (
    <Lab
      title="Which suggestion counts as right, and how does the code decide?"
      eyebrow="Golden set · matching"
      className="rel-lab eval-lab"
    >
      <Predict>
        Which suggestions match under each rule? Switch Matching rule, then press Add note 2's
        title.
      </Predict>
      <div className="cb-controls">
        <label>
          Expected task
          <ModelSelect
            aria-label="Expected task"
            value={state.expected}
            onValueChange={(value) => act({ type: 'expected', value: toExpectedTaskId(value) })}
            options={matchExpectedTasks(state).map((entry) => ({
              value: entry.id,
              label: entry.title,
            }))}
          />
        </label>
        <label>
          Matching rule
          <ModelSelect
            aria-label="Matching rule"
            value={state.rule}
            onValueChange={(value) => act({ type: 'rule', value: toMatchRule(value) })}
            options={MATCH_RULES.map((rule) => ({ value: rule, label: matchRuleLabels[rule] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.withNoteTwo}
          onClick={() => act({ type: 'toggle-note-two' })}
        >
          Add note 2's title
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Suggestions"
        columns={['Suggestion', `Under ${matchRuleLabels[state.rule]}`]}
        rows={matchRows(state).map((row) => [row.candidate, matchText(row.matched)])}
        template="minmax(0, 2fr) minmax(0, 1fr)"
      />
      <Facts
        values={[
          ['Expected task', task.title],
          ['Keywords', task.keywords.join(', ')],
          ['Verdict', matchVerdict(state.rule)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function EvalRunLab() {
  const [state, setState] = useState(initialRun);
  const act = (action: RunAction) => setState((current) => runTransition(current, action));
  const notes = gradedNotes(state);
  const score = state.view === 'score';
  const summary = runSummary(state);
  return (
    <Lab
      title="What does one run of the set tell us?"
      eyebrow="Golden set · one run"
      className="rel-lab eval-lab"
    >
      <Predict>
        How many of the 30 notes will pass? Press Grade the next note, then change Show results as.
      </Predict>
      <div className="cb-controls">
        <Button size="sm" disabled={!canGradeNext(state)} onClick={() => act({ type: 'next' })}>
          Grade the next note
        </Button>
        <label>
          Show results as
          <ModelSelect
            aria-label="Show results as"
            value={state.view}
            onValueChange={(value) => act({ type: 'view', value: toRunView(value) })}
            options={RUN_VIEWS.map((view) => ({ value: view, label: runViewLabels[view] }))}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Run 1"
        columns={score ? ['Note', 'Title', 'Score'] : ['Note', 'Title', 'Verdict', 'Reasons']}
        rows={notes.map((note) =>
          score
            ? [note.id, note.title, scoreFor(note)]
            : [note.id, note.title, note.verdict, reasonsText(note)],
        )}
        template={
          score ? '56px minmax(0, 2fr) minmax(0, 1fr)' : '56px minmax(0, 1fr) 72px minmax(0, 2fr)'
        }
        className={score ? '' : 'eval-wide'}
        empty="Nothing graded yet"
      />
      <Facts
        values={[
          ['Graded', `${notes.length} of ${NOTE_COUNT}`],
          ...(notes.length ? [['Running total', runningLine(state)] as [string, string]] : []),
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function EvalCodingLab() {
  const [state, setState] = useState(initialCoding);
  const act = (action: CodingAction) => setState((current) => codingTransition(current, action));
  const summary = codingSummary(state);
  return (
    <Lab
      title="What kind of failure is each one?"
      eyebrow="Golden set · failure modes"
      className="rel-lab eval-lab"
    >
      <Predict>
        Which kind of failure is most common? Set a Failure mode for each note and watch the Mode
        table.
      </Predict>
      <div className="cb-controls">
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        {FAILURE_CARDS.map((card) => (
          <Panel key={card.id} label={`Note ${card.id} · ${card.title}`}>
            <p>{card.reason}</p>
            <div className="cb-controls">
              <label>
                Failure mode
                <ModelSelect
                  aria-label={`Failure mode for note ${card.id}`}
                  value={state[card.id]}
                  onValueChange={(value) =>
                    act({ type: 'assign', id: card.id, mode: toFailureMode(value) })
                  }
                  options={FAILURE_MODES.map((mode) => ({
                    value: mode,
                    label: failureModeLabels[mode],
                  }))}
                />
              </label>
            </div>
          </Panel>
        ))}
      </div>
      <Panel label="Mode table">
        <pre aria-label="Mode table">{modeTable(state).map(modeLine).join('\n')}</pre>
      </Panel>
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function EvalSamplingLab() {
  const [state, setState] = useState(initialSampling);
  const note = SAMPLED_NOTES[state.note];
  const runs = shownRuns(state);
  const summary = samplingSummary(state);
  const panels = runs.map((run) => (
    <Panel key={run.label} label={run.label}>
      <pre aria-label={run.label}>{runText(run.record)}</pre>
    </Panel>
  ));
  return (
    <Lab
      title="Is one run the answer?"
      eyebrow="Golden set · sampling"
      className="rel-lab eval-lab"
    >
      <Predict>
        Will run 2 give the same verdict? Set Run to Both runs, then try another Note.
      </Predict>
      <div className="cb-controls">
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={String(state.note)}
            onValueChange={(value) =>
              setState((current) => ({ ...current, note: toSamplingNoteId(value) }))
            }
            options={SAMPLING_NOTE_IDS.map((id) => ({
              value: String(id),
              label: `${id} ${SAMPLED_NOTES[id].title}`,
            }))}
          />
        </label>
        <label>
          Run
          <ModelSelect
            aria-label="Run"
            value={state.run}
            onValueChange={(value) =>
              setState((current) => ({ ...current, run: toSamplingRun(value) }))
            }
            options={SAMPLING_RUNS.map((run) => ({ value: run, label: samplingRunLabels[run] }))}
          />
        </label>
        <Reset onClick={() => setState(initialSampling())} />
      </div>
      {runs.length === 2 ? <div className="cb-two">{panels}</div> : panels}
      <Facts
        values={[
          ['Note', `${note.id} ${note.title}`],
          ...(state.run === 'both'
            ? [['Between the runs', agreementLine(note)] as [string, string]]
            : []),
        ]}
      />
      <Panel label="Two runs, 30 notes">
        <p>{totalsLine()}</p>
      </Panel>
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
